import React, { useState } from 'react';
import { useAuthorDashboard } from '../AuthorDashboardContext';
import { db } from '../../firebase';
import { collection, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Bell, Plus, Send, Eye, EyeOff, BookOpen, Users, ChevronDown, ChevronUp
} from 'lucide-react';

const READER_STATUSES = [
  'All Readers',
  'Street Team',
  'ARC Sent',
  'ARC Approved',
  'Noted ARC interest',
];

export default function AuthorAnnouncementsTab() {
  const { books, announcements, basePath } = useAuthorDashboard();
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [bookId, setBookId] = useState('all');
  const [targetStatuses, setTargetStatuses] = useState(['All Readers']);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const activeBooks = books.filter(b => b.status !== 'archived');

  function toggleStatus(status) {
    if (status === 'All Readers') {
      setTargetStatuses(['All Readers']);
      return;
    }
    let next = targetStatuses.filter(s => s !== 'All Readers');
    if (next.includes(status)) {
      next = next.filter(s => s !== status);
    } else {
      next.push(status);
    }
    if (next.length === 0) next = ['All Readers'];
    setTargetStatuses(next);
  }

  async function handleSend() {
    if (!title.trim() || !body.trim()) {
      setSaveMessage('Title and message are required.');
      return;
    }
    setIsSaving(true);
    setSaveMessage('');
    try {
      await addDoc(collection(db, basePath, 'announcements'), {
        title: title.trim(),
        body: body.trim(),
        bookId: bookId || 'all',
        targetStatuses,
        active: true,
        createdAt: serverTimestamp(),
      });
      setTitle('');
      setBody('');
      setBookId('all');
      setTargetStatuses(['All Readers']);
      setShowForm(false);
      setSaveMessage('Announcement sent!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      setSaveMessage(`Error: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  }

  async function toggleActive(announcement) {
    try {
      await updateDoc(doc(db, basePath, 'announcements', announcement.id), {
        active: !announcement.active,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      alert('Error: ' + err.message);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Announcements</h1>
        <Button onClick={() => setShowForm(!showForm)} className="gap-1.5 bg-teal-600 text-white hover:bg-teal-700">
          <Plus className="h-4 w-4" /> New Announcement
        </Button>
      </div>

      {saveMessage && (
        <div className={`rounded-lg p-3 text-sm ${saveMessage.startsWith('Error') ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-green-50 text-green-600 border border-green-200'}`}>
          {saveMessage}
        </div>
      )}

      {/* Compose form */}
      {showForm && (
        <Card className="border-teal-200 bg-teal-50/30">
          <CardContent className="p-5 space-y-4">
            <h3 className="font-semibold text-slate-900">Compose Announcement</h3>
            <div>
              <Label>Title *</Label>
              <Input value={title} onChange={e => setTitle(e.target.value)}
                placeholder="e.g. ARC copies shipping this week!" className="mt-1" />
            </div>
            <div>
              <Label>Message *</Label>
              <Textarea value={body} onChange={e => setBody(e.target.value)}
                placeholder="Write your announcement here..." rows={4} className="mt-1" />
            </div>
            <div>
              <Label>Book</Label>
              <div className="mt-1 flex flex-wrap gap-2">
                <button onClick={() => setBookId('all')}
                  className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${bookId === 'all' ? 'border-teal-300 bg-teal-50 text-teal-800' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>
                  All Books
                </button>
                {activeBooks.map(b => (
                  <button key={b.id} onClick={() => setBookId(b.id)}
                    className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${bookId === b.id ? 'border-teal-300 bg-teal-50 text-teal-800' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>
                    {b.title}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Target Readers</Label>
              <p className="text-xs text-slate-400 mb-2">Select which readers should see this announcement.</p>
              <div className="flex flex-wrap gap-2">
                {READER_STATUSES.map(status => (
                  <button key={status} onClick={() => toggleStatus(status)}
                    className={`rounded-full px-3 py-1 text-sm font-medium transition-all ${targetStatuses.includes(status) ? 'bg-teal-600 text-white' : 'border border-slate-200 bg-white text-slate-600 hover:border-teal-300'}`}>
                    {status}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSend} disabled={isSaving || !title.trim() || !body.trim()}
                className="gap-1.5 bg-teal-600 text-white hover:bg-teal-700">
                <Send className="h-4 w-4" /> {isSaving ? 'Sending...' : 'Send Announcement'}
              </Button>
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Existing announcements */}
      {announcements.length === 0 ? (
        <Card className="p-8 text-center">
          <Bell className="mx-auto mb-2 h-10 w-10 text-slate-300" />
          <p className="text-slate-500">No announcements yet. Create one to message your readers.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {announcements.map(ann => {
            const bookTitle = ann.bookId === 'all' ? 'All Books' : books.find(b => b.id === ann.bookId)?.title || ann.bookId;
            const dateStr = ann.createdAt?.toDate
              ? ann.createdAt.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
              : '—';
            return (
              <Card key={ann.id} className={`overflow-hidden ${ann.active ? 'border-slate-200' : 'border-slate-100 opacity-60'}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-slate-900">{ann.title}</h3>
                        <Badge className={ann.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>
                          {ann.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600 whitespace-pre-wrap">{ann.body}</p>
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                        <span className="flex items-center gap-1"><BookOpen className="h-3 w-3" /> {bookTitle}</span>
                        <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {(ann.targetStatuses || ['All Readers']).join(', ')}</span>
                        <span>{dateStr}</span>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => toggleActive(ann)}
                      className={ann.active ? 'text-slate-400 hover:text-red-500' : 'text-slate-400 hover:text-green-600'}
                      title={ann.active ? 'Deactivate' : 'Reactivate'}>
                      {ann.active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
